import { FC, ReactNode } from "react";
import {
	Box,
	MenuItem,
	OutlinedInput,
	Paper,
	Select,
	Stack,
	Typography,
} from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { Program } from "../../../../domain/program.interface";

/** Props shared by every Reports Center tab — program is selected per report. */
export interface ReportTabProps {
	programId: string;
	programs: Program[];
	onProgramChange: (id: string) => void;
}

export const programNameOf = (
	programs: Program[],
	id: string,
): string | undefined => programs.find((p) => p._id === id)?.name;

/**
 * SIMS-7396: the payers configured for a program (`program.payers`) intersected
 * with the account's active payers. Returns [] when the program has no payers
 * configured — callers disable the Payer filter in that case.
 */
export const payersForProgram = <T extends { _id: string }>(
	activePayers: readonly T[] | undefined,
	program: Pick<Program, "payers"> | undefined,
): T[] => {
	const ids = program?.payers ?? [];
	if (ids.length === 0) return [];
	return (activePayers ?? []).filter((p) => ids.includes(p._id));
};

/**
 * SIMS-7397: the org-unit ids the user has for a given program (from
 * userInfo.programs) — the OU scope the Beneficiary Report uses.
 */
export const programOrgUnitIds = (
	userPrograms:
		| ReadonlyArray<{
				programId: string;
				orgUnitId?: ReadonlyArray<string | { orgUnitId: string }>;
		  }>
		| undefined,
	programId: string,
): string[] =>
	(userPrograms ?? [])
		.filter((p) => p.programId === programId)
		// SIMS-7397: orgUnitId entries can be either a plain id string or an
		// { orgUnitId } object at runtime — handle both, mirroring the Beneficiary
		// Report. (Assuming only the object shape returned undefined ids, which
		// left the Group Labels list empty.)
		.flatMap((p) =>
			(p.orgUnitId ?? []).map((o) => (typeof o === "string" ? o : o.orgUnitId)),
		)
		.filter((id): id is string => Boolean(id));

/**
 * SIMS-7397: the group labels attached to the given org-unit sites. Used to
 * scope the Group Labels filter to the selected program's OUs, mirroring the
 * Group Labels logic in the Beneficiary Report.
 */
export const groupLabelsForSites = <T extends { _id: string }>(
	groupLabels: readonly T[] | undefined,
	sites: ReadonlyArray<{ groupLabelIds?: string[] }> | undefined,
): T[] => {
	const ids = (sites ?? []).flatMap((s) => s.groupLabelIds ?? []);
	return (groupLabels ?? []).filter((l) => ids.includes(l._id));
};

/** Uppercase label-above-control field used across the report filter bars. */
export const FilterField: FC<{
	label: string;
	required?: boolean;
	badge?: string;
	labelColor: string;
	errorColor?: string;
	children: ReactNode;
}> = ({ label, required, badge, labelColor, errorColor, children }) => (
	<Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
		<Stack direction="row" spacing={1} alignItems="center">
			<Typography
				sx={{
					fontSize: 12,
					fontWeight: 600,
					letterSpacing: "0.06em",
					color: labelColor,
				}}
			>
				{label.toUpperCase()}
				{required && (
					<Box component="span" sx={{ color: errorColor, ml: 0.5 }}>
						*
					</Box>
				)}
			</Typography>
			{badge && (
				<Box
					component="span"
					sx={{
						bgcolor: "primary.light",
						color: "primary.main",
						fontSize: 10,
						fontWeight: 700,
						letterSpacing: "0.04em",
						px: 1,
						py: 0.25,
						borderRadius: "9999px",
					}}
				>
					{badge}
				</Box>
			)}
		</Stack>
		{children}
	</Box>
);

/** Required Program selector rendered as the first filter in every report. */
export const ProgramFilterField: FC<{
	programId: string;
	programs: Program[];
	onProgramChange: (id: string) => void;
	labelColor: string;
	mutedColor: string;
	errorColor: string;
}> = ({
	programId,
	programs,
	onProgramChange,
	labelColor,
	mutedColor,
	errorColor,
}) => (
	<FilterField
		label="Program"
		required
		labelColor={labelColor}
		errorColor={errorColor}
	>
		<Select
			size="small"
			displayEmpty
			value={programId}
			input={<OutlinedInput />}
			onChange={(e) => onProgramChange(e.target.value)}
			renderValue={(v) => {
				// SIMS-7390: a program is always selected — no empty/placeholder
				// option. The muted fallback only shows when the user has no
				// assigned programs at all.
				const name = programs.find((p) => p._id === v)?.name;
				return (
					name ?? (
						<Typography sx={{ color: mutedColor }}>No programs</Typography>
					)
				);
			}}
		>
			{programs.map((p) => (
				<MenuItem key={p._id} value={p._id}>
					{p.name}
				</MenuItem>
			))}
		</Select>
	</FilterField>
);

/** "No program selected" result state (FR-08 — report does not render). */
export const NoProgramCard: FC<{ borderColor: string; mutedColor: string }> = ({
	borderColor,
	mutedColor,
}) => (
	<Paper
		variant="outlined"
		sx={{ borderColor, borderRadius: 3, py: 9, px: 3, textAlign: "center" }}
	>
		<FilterAltOutlinedIcon sx={{ fontSize: 48, color: mutedColor }} />
		<Typography sx={{ fontSize: 20, mt: 1 }}>
			Select a program to run the report.
		</Typography>
		<Typography sx={{ fontSize: 15, color: mutedColor, mt: 0.5 }}>
			A program is required before any report data can be shown.
		</Typography>
	</Paper>
);
